import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Upload, FileSpreadsheet, Loader2, X, CheckCircle2, AlertTriangle, XCircle, CalendarClock, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';
import { ErroresImportacion, AdvertenciasImportacion, ImportacionCancelada } from './ImportResultadoDetalle';

const ImportarEntregasModal = ({ open, onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [progress, setProgress] = useState(0);
  const [progressDetail, setProgressDetail] = useState('');
  const [horaEstimadaFin, setHoraEstimadaFin] = useState(null);
  const [jobState, setJobState] = useState(null);
  const [polling, setPolling] = useState(false);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  // Polling: el estado se lee siempre desde la auditoría persistida (todas las etapas)
  const pollStatus = useCallback(async (id) => {
    try {
      const resp = await api.get(`/entregas/ingest/status/${id}`);
      const d = resp.data;
      setProgress(d.progress || 0);
      setProgressDetail(d.detail || '');
      setHoraEstimadaFin(d.hora_estimada_fin || null);
      setJobState(d);
      if (d.result) setResult(d.result);

      if (d.status === 'procesando') return false;
      setImporting(false);
      setPolling(false);
      setHoraEstimadaFin(null);
      if (d.status === 'completado') {
        if (d.result?.importados > 0 && (d.result.errores || []).length === 0) {
          toast.success(`Se importaron ${d.result.importados} entregas exitosamente`);
        }
      } else if (d.status === 'completado_con_advertencias') {
        toast.warning(`Importación completada con ${(d.result?.advertencias || []).length} advertencia(s)`);
      } else {
        toast.error(d.error || 'Error durante la importación');
      }
      return true;
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    if (!polling || !jobId) return;
    const interval = setInterval(async () => {
      const done = await pollStatus(jobId);
      if (done) clearInterval(interval);
    }, 2000);
    pollRef.current = interval;
    return () => clearInterval(interval);
  }, [polling, jobId, pollStatus]);

  if (!open) return null;

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) {
      if (!f.name.toLowerCase().endsWith('.xlsx')) { toast.error('Solo se aceptan archivos .xlsx'); return; }
      setFile(f); setResult(null); setJobId(null); setProgress(0); setJobState(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) {
      if (!f.name.toLowerCase().endsWith('.xlsx')) { toast.error('Solo se aceptan archivos .xlsx'); return; }
      setFile(f); setResult(null); setJobId(null); setProgress(0);
    }
  };

  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    setResult(null);
    setJobState(null);
    setProgress(0);
    setProgressDetail('Subiendo archivo...');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const resp = await api.post('/entregas/ingest/excel', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000
      });
      const d = resp.data;
      if (d.job_id) {
        setJobId(d.job_id);
        setPolling(true);
        setProgressDetail(d.message || 'Procesando en segundo plano...');
        setProgress(5);
      } else if (d.total_filas !== undefined) {
        // Legacy sync response (shouldn't happen but fallback)
        setResult(d);
        setImporting(false);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (detail ? JSON.stringify(detail) : 'Error al procesar el archivo'));
      setImporting(false);
    }
  };

  const handleClose = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (result && (result.importados > 0 || result.actualizados > 0)) {
      onSuccess?.();
    }
    setFile(null); setResult(null); setJobId(null); setPolling(false); setProgress(0); setJobState(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="modal-importar">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E8E8E7]">
          <div className="flex items-center gap-2">
            <FileSpreadsheet size={18} className="text-[#4E7A79]" />
            <h2 className="text-base font-semibold text-[#2D2D2D]">Importar Entregas desde Excel</h2>
          </div>
          <Button variant="ghost" size="sm" onClick={handleClose} className="h-7 w-7 p-0" disabled={importing && !result}>
            <X size={16} />
          </Button>
        </div>

        {/* Content */}
        <div className="px-5 py-4 overflow-y-auto flex-1">
          {importing && !result ? (
            /* Progress view */
            <div className="space-y-4 py-6">
              <div className="text-center">
                <Loader2 size={32} className="animate-spin text-[#4E7A79] mx-auto mb-3" />
                <p className="text-sm font-medium text-[#2D2D2D]">Procesando importación...</p>
                <p className="text-xs text-[#888] mt-1">{progressDetail}</p>
                {horaEstimadaFin && (
                  <p className="text-xs text-[#4E7A79] font-medium mt-2" data-testid="hora-estimada-fin">
                    Hora estimada de finalización: {horaEstimadaFin}
                  </p>
                )}
              </div>
              <div className="w-full bg-[#E8E8E7] rounded-full h-3 overflow-hidden" data-testid="progress-bar">
                <div
                  className="h-full bg-[#4E7A79] rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-center text-xs text-[#888]">{progress}%</p>
            </div>
          ) : !result ? (
            <>
              {/* Instructions */}
              <div className="text-sm text-[#888] mb-3 space-y-2">
                <p>Sube el archivo Excel exportado desde Sperant con las columnas requeridas:</p>
                <div className="bg-[#F8F8F8] rounded-lg p-3 text-xs space-y-1">
                  <p className="font-medium text-[#5A5A5A]">Columnas obligatorias:</p>
                  <p className="text-[#888]">Proforma, Unidad P., Unidades asignadas, Proyecto, Cliente, N° Documento, Email, Teléfono fijo, Celular</p>
                  <p className="font-medium text-[#5A5A5A] mt-2">Columna opcional:</p>
                  <p className="text-[#888]">Premio</p>
                  <div className="border-t border-[#E8E8E7] my-2 pt-2">
                    <p className="font-medium text-[#4E7A79] flex items-center gap-1">
                      <CalendarClock size={12} />
                      Columnas de programación (opcionales):
                    </p>
                    <p className="text-[#888] mt-1">
                      Fecha1 de entrega, Horario1 de entrega, Fecha2 de entrega, Horario2 de entrega, Fecha3 de entrega, Horario3 de entrega
                    </p>
                    <p className="text-[10px] text-[#aaa] mt-1">
                      Si se incluyen, las citas se crean con duración de 2 horas y se asigna arquitecto por round-robin del área Entregas.
                    </p>
                  </div>
                </div>
              </div>

              {/* Dropzone / File selected */}
              {!file ? (
                <div
                  onDragOver={e => e.preventDefault()}
                  onDrop={handleDrop}
                  onClick={() => fileRef.current?.click()}
                  className="border-2 border-dashed border-[#D5D5D5] rounded-lg p-8 text-center cursor-pointer hover:border-[#9d8272] transition-colors"
                  data-testid="dropzone"
                >
                  <Upload size={32} className="mx-auto text-[#aaa] mb-2" />
                  <p className="text-sm text-[#888]">
                    Arrastra tu archivo .xlsx aquí o <span className="text-[#9d8272] font-medium">click para seleccionar</span>
                  </p>
                  <input ref={fileRef} type="file" accept=".xlsx" onChange={handleFileChange} className="hidden" data-testid="input-file" />
                </div>
              ) : (
                <div className="flex items-center gap-2 p-3 bg-[#F8F8F8] rounded-lg border border-[#E8E8E7]">
                  <FileSpreadsheet size={18} className="text-green-600" />
                  <span className="text-sm text-[#2D2D2D] flex-1 truncate">{file.name}</span>
                  <span className="text-xs text-[#888]">{(file.size / 1024).toFixed(1)} KB</span>
                  <Button variant="ghost" size="sm" onClick={() => setFile(null)} className="h-6 w-6 p-0"><X size={12} /></Button>
                </div>
              )}
            </>
          ) : (
            /* Results */
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <ResultCard icon={<FileSpreadsheet size={16} />} label="Total filas" value={result.total_filas} color="#5A5A5A" />
                <ResultCard icon={<CheckCircle2 size={16} />} label="Creados" value={result.importados} color="#10B981" />
                <ResultCard icon={<RefreshCw size={16} />} label="Actualizados" value={result.actualizados || 0} color="#3B82F6" />
                <ResultCard icon={<AlertTriangle size={16} />} label="Duplicados" value={(result.duplicados_existentes || 0) + (result.duplicados_archivo || 0)} color="#F59E0B" />
                <ResultCard icon={<CalendarClock size={16} />} label="Citas creadas" value={result.citas_programadas || 0} color="#4E7A79" />
                <ResultCard icon={<XCircle size={16} />} label="Errores" value={result.errores_count ?? (result.errores || []).length} color="#EF4444" />
              </div>

              <ImportStagePanel job={jobState} result={result} />

              {result.crm_enriquecimiento === 'completado' && result.crm_sincronizados > 0 && (
                <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg text-sm text-green-700">
                  <CheckCircle2 size={14} />
                  <span>CRM sincronizado: {result.crm_sincronizados} entregas enriquecidas</span>
                </div>
              )}

              {(result.correcciones_unidad || []).length > 0 && (
                <div className="border border-amber-200 rounded-lg overflow-hidden" data-testid="correcciones-unidad">
                  <div className="bg-amber-50 px-3 py-2 text-xs font-medium text-amber-700 flex items-center gap-1">
                    <AlertTriangle size={12} />
                    {result.total_correcciones_unidad || result.correcciones_unidad.length} unidades corregidas por CRM
                  </div>
                  <div className="max-h-[180px] overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b bg-[#FAFAFA] sticky top-0">
                          <th className="text-left p-2 font-medium">Proforma</th>
                          <th className="text-left p-2 font-medium">Excel</th>
                          <th className="text-center p-2 font-medium w-8"></th>
                          <th className="text-left p-2 font-medium">CRM</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.correcciones_unidad.map((c, i) => (
                          <tr key={i} className="border-b border-[#F0F0F0]">
                            <td className="p-2 font-mono text-amber-700">{c.proforma}</td>
                            <td className="p-2 text-[#888] line-through">{c.unidad_excel}</td>
                            <td className="p-2 text-center text-[#888]">→</td>
                            <td className="p-2 text-[#2D2D2D] font-medium">{c.unidad_crm}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {jobState?.status === 'cancelado' && <ImportacionCancelada mensaje={jobState.error} />}

              {(result.errores || []).length > 0 && (
                <ErroresImportacion jobId={jobId} errores={result.errores} total={result.errores_count} />
              )}

              {(result.advertencias || []).length > 0 && (
                <AdvertenciasImportacion jobId={jobId} advertencias={result.advertencias}
                  onReintentado={() => setTimeout(() => pollStatus(jobId), 8000)} />
              )}

              {jobState?.status === 'completado' && result.importados > 0 && (result.errores || []).length === 0 && (
                <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg text-sm text-green-700">
                  <CheckCircle2 size={16} />
                  <span>Todas las entregas se importaron correctamente</span>
                </div>
              )}

              {jobState?.status === 'completado_con_advertencias' && (result.importados > 0 || result.actualizados > 0) && (
                <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg text-sm text-green-700" data-testid="import-exito-con-advertencias">
                  <CheckCircle2 size={16} />
                  <span>
                    Importación guardada: {result.importados} entrega(s) creada(s)
                    {result.actualizados > 0 ? ` y ${result.actualizados} actualizada(s)` : ''}. Revise las advertencias de abajo.
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-[#E8E8E7]">
          {!result && !importing ? (
            <>
              <Button variant="outline" onClick={handleClose}>Cancelar</Button>
              <Button
                onClick={handleImport}
                disabled={!file || importing}
                className="bg-[#9d8272] hover:bg-[#8a7162] text-white min-w-[120px]"
                data-testid="btn-importar"
              >
                <Upload size={14} className="mr-1" /> Importar
              </Button>
            </>
          ) : result ? (
            <Button onClick={handleClose} className="bg-[#4E7A79] hover:bg-[#3d6362] text-white" data-testid="btn-cerrar-resultado">
              Cerrar
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const Barra = ({ value, total, color }) => (
  <div className="w-full bg-white/70 rounded-full h-2 overflow-hidden">
    <div className="h-full rounded-full transition-all duration-500 ease-out"
      style={{ width: `${total ? Math.round((value / total) * 100) : 0}%`, backgroundColor: color }} />
  </div>
);

const ImportStagePanel = ({ job, result }) => {
  if (!job) return null;
  const notif = result?.notificacion;
  return (
    <div className="space-y-2" data-testid="import-stage-panel">
      {job.status === 'procesando' && job.reanudando && (
        <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg text-sm text-amber-700" data-testid="import-reanudando">
          <RefreshCw size={14} className="animate-spin flex-shrink-0" />
          <span>{job.detail}</span>
        </div>
      )}
      {job.status === 'procesando' && !job.reanudando && job.etapa === 'crm' && (
        <div className="space-y-2 p-3 bg-blue-50 rounded-lg" data-testid="crm-progress">
          <div className="flex items-center gap-2 text-sm text-blue-700">
            <RefreshCw size={14} className="animate-spin flex-shrink-0" />
            <span className="font-medium">
              Enriquecimiento CRM: {job.crm_total ? `${job.crm_procesados || 0}/${job.crm_total}` : '...'}
              {job.crm_codigo_actual ? ` — ${job.crm_codigo_actual}` : ''}
            </span>
          </div>
          {job.crm_total > 0 && <Barra value={job.crm_procesados || 0} total={job.crm_total} color="#3B82F6" />}
        </div>
      )}
      {job.status === 'procesando' && !job.reanudando && job.etapa === 'pdf' && (
        <div className="space-y-2 p-3 bg-blue-50 rounded-lg" data-testid="pdf-progress">
          <div className="flex items-center gap-2 text-sm text-blue-700">
            <RefreshCw size={14} className="animate-spin flex-shrink-0" />
            <span className="font-medium">
              Leyendo fechas de PDFs: {job.pdf_total ? `${job.pdf_procesados || 0}/${job.pdf_total}` : '...'}
            </span>
          </div>
          {job.pdf_total > 0 && <Barra value={job.pdf_procesados || 0} total={job.pdf_total} color="#3B82F6" />}
        </div>
      )}
      {job.status === 'procesando' && !job.reanudando && job.etapa === 'notificando' && (
        <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg text-sm text-blue-700" data-testid="notif-progress">
          <RefreshCw size={14} className="animate-spin flex-shrink-0" />
          <span className="font-medium">
            Enviando resumen de notificación{notif?.destinatarios ? `: ${notif.enviados || 0}/${notif.destinatarios}` : '...'}
          </span>
        </div>
      )}
      {notif?.estado === 'completado' && (
        <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg text-sm text-green-700" data-testid="notif-resultado">
          <CheckCircle2 size={14} />
          <span>Resumen de notificación enviado a {notif.enviados || 0} de {notif.destinatarios} destinatarios</span>
        </div>
      )}
      {result?.insercion_incompleta && (
        <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg text-sm text-amber-700" data-testid="insercion-incompleta">
          <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
          <span>
            La creación de entregas se interrumpió: se crearon {result.importados} de {result.total_nuevas ?? '?'}.
            Vuelva a subir el mismo Excel para completar las faltantes.
          </span>
        </div>
      )}
      {(job.status === 'error' || job.status === 'interrumpido') && (
        <div className="flex items-start gap-2 p-3 bg-red-50 rounded-lg text-sm text-red-700" data-testid="import-error">
          <XCircle size={14} className="mt-0.5 flex-shrink-0" />
          <span>{job.error || 'La importación no pudo completarse'}</span>
        </div>
      )}
    </div>
  );
};

const ResultCard = ({ icon, label, value, color }) => (
  <div className="text-center p-2 bg-[#F8F8F8] rounded-lg">
    <div className="flex items-center justify-center mb-1" style={{ color }}>{icon}</div>
    <div className="text-lg font-bold" style={{ color }}>{value}</div>
    <div className="text-[10px] text-[#888]">{label}</div>
  </div>
);

export default ImportarEntregasModal;
